import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { gbp, toDisplay, toGbp, curSymbol } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { PiggyBank, Pencil, Info } from "lucide-react";
import { CardHead, Field, FormDialog } from "@/components/prohub/shared";

const KEYS = ["current_pot", "monthly_contribution", "employer_contribution", "age", "retirement_age"];
const MONEY = ["current_pot", "monthly_contribution", "employer_contribution"];

export default function PensionCard({ data, onChanged }) {
  const { pension: p, projection: pr } = data;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const openEdit = () => {
    setForm(p ? Object.fromEntries(KEYS.map((k) => [k, String(MONEY.includes(k) ? toDisplay(p[k]) : p[k])])) : { current_pot: "", monthly_contribution: "", employer_contribution: "", age: "", retirement_age: "67" });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/pro-hub/pension", Object.fromEntries(KEYS.map((k) => [k, MONEY.includes(k) ? toGbp(form[k]) : Number(form[k] || 0)])));
      toast.success("Pension details saved");
      setOpen(false);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const valid = form.age && form.retirement_age && Number(form.retirement_age) > Number(form.age);

  return (
    <div className="mm-glow-card p-6 h-full" data-testid="pro-pension-card">
      <CardHead icon={PiggyBank} title="Pension" sub={p ? `${gbp(p.monthly_contribution + p.employer_contribution)} going in each month` : "Track your pot and see where it's heading"}
        action={p && <button data-testid="pro-edit-pension" onClick={openEdit} className="text-slate-400 hover:text-white transition-colors"><Pencil className="h-4 w-4" /></button>} />
      {p ? (
        <div className="mt-5">
          <p className="text-xs text-slate-500">Current pot</p>
          <p data-testid="pro-pension-pot" className="font-num text-3xl font-extrabold mt-1">{gbp(p.current_pot)}</p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <div className="rounded-xl border border-white/8 bg-[#0D0D0D] p-3"><p className="text-[10px] text-slate-500">You pay</p><p className="font-num text-sm font-bold mt-0.5">{gbp(p.monthly_contribution)}/mo</p></div>
            <div className="rounded-xl border border-white/8 bg-[#0D0D0D] p-3"><p className="text-[10px] text-slate-500">Employer adds</p><p className="font-num text-sm font-bold mt-0.5 text-[#4DE3BF]">{gbp(p.employer_contribution)}/mo</p></div>
          </div>
          <div className="mt-4 rounded-xl border border-[#00C896]/25 bg-[#00C896]/[0.06] p-4">
            <p className="text-[11px] uppercase tracking-[0.14em] text-[#4DE3BF]">Estimated at age {p.retirement_age}</p>
            <p data-testid="pro-pension-projection" className="font-num text-2xl font-extrabold mt-1">{gbp(pr.projected_pot)}</p>
            <p className="text-[11px] text-slate-400 mt-1">{gbp(pr.total_contributed)} paid in over {pr.years} years</p>
          </div>
          <p data-testid="pro-pension-disclaimer" className="mt-3 flex gap-1.5 text-[10px] text-slate-500 leading-relaxed">
            <Info className="h-3 w-3 shrink-0 mt-0.5" /> Estimate only. Assumes {Math.round(pr.growth_rate * 100)}% yearly growth with no fees, inflation or contribution changes. Not financial advice.
          </p>
        </div>
      ) : (
        <div className="mt-5 rounded-xl border border-dashed border-white/12 p-6 text-center">
          <p className="text-sm text-slate-400">Add your workplace or personal pension to see an estimated pot at retirement.</p>
          <Button data-testid="pro-set-pension" onClick={openEdit} variant="outline" className="mt-3 rounded-full border-[#00C896]/40 bg-transparent text-[#00C896] hover:bg-[#00C896]/10">Add pension</Button>
        </div>
      )}
      <FormDialog open={open} onOpenChange={setOpen} title="Pension details" testid="pro-pension-modal" onSave={save} saving={saving} disabled={!valid} saveLabel="Save pension">
        <Field label={`Current pot (${curSymbol()})`} testid="pro-pension-pot-input" type="number" min="0" value={form.current_pot || ""} onChange={upd("current_pot")} placeholder="18000" />
        <div className="grid grid-cols-2 gap-3">
          <Field label={`You pay / month (${curSymbol()})`} testid="pro-pension-you-input" type="number" min="0" value={form.monthly_contribution || ""} onChange={upd("monthly_contribution")} placeholder="200" />
          <Field label={`Employer / month (${curSymbol()})`} testid="pro-pension-employer-input" type="number" min="0" value={form.employer_contribution || ""} onChange={upd("employer_contribution")} placeholder="150" />
          <Field label="Your age" testid="pro-pension-age-input" type="number" min="16" max="80" value={form.age || ""} onChange={upd("age")} placeholder="32" />
          <Field label="Retire at" testid="pro-pension-retire-input" type="number" min="50" max="80" value={form.retirement_age || ""} onChange={upd("retirement_age")} placeholder="67" />
        </div>
      </FormDialog>
    </div>
  );
}
